import React, { ComponentProps } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_300,
  COLOR_PRIMARY_900,
  COLOR_SECONDARY_200,
  COLOR_SECONDARY_50,
} from '../../../constants';

/** Enable type-to-filter once a select can become hard to scan. */
const SEARCHABLE_THRESHOLD = 8;

interface DropdownOption<T> {
  label: string;
  value: T;
}

type DropdownSelectProps<T> = {
  options: DropdownOption<T>[];
  setValue: (value: T | undefined) => void;
  value: T | undefined;
} & Omit<ComponentProps<typeof Select<DropdownOption<T>>>, 'value'>;

export default function DropdownSelect<T>({
  options,
  setValue,
  value,
  styles: stylesProp,
  isSearchable,
  ...props
}: DropdownSelectProps<T>) {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { control, option, indicatorSeparator, dropdownIndicator, ...styles } = stylesProp ?? {};
  const searchable = isSearchable ?? options.length >= SEARCHABLE_THRESHOLD;

  return (
    <Select
      styles={{
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderColor: state.isFocused ? COLOR_PRIMARY_300 : `${COLOR_SECONDARY_200}80`,
          borderRadius: '15px',
          borderWidth: '2px',
          color: COLOR_PRIMARY_900,
          backgroundColor: `${COLOR_SECONDARY_200}80`,
          boxShadow: 'none',
          minHeight: '50px',
          fontWeight: '500',
          fontSize: '16px',
          '&:hover': {
            borderColor: state.isFocused ? COLOR_PRIMARY_300 : `${COLOR_SECONDARY_200}80`,
          },
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          margin: 0,
          padding: 0,
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          overflow: 'hidden',
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          zIndex: 9999,
        }),
        menuPortal: (baseStyles) => ({
          ...baseStyles,
          zIndex: 9999,
        }),
        option: (baseStyles, { isSelected }) => ({
          ...baseStyles,
          backgroundColor: isSelected ? COLOR_PRIMARY : COLOR_SECONDARY_50,
          color: isSelected ? COLOR_SECONDARY_50 : COLOR_PRIMARY_900,
          '&:hover': {
            backgroundColor: isSelected ? COLOR_PRIMARY_300 : COLOR_PRIMARY_300,
            color: COLOR_PRIMARY_900,
          },
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        dropdownIndicator: (baseStyles, state) => ({
          ...baseStyles,
          color: state.isFocused ? COLOR_PRIMARY_300 : COLOR_PRIMARY,
        }),
        ...styles,
      }}
      options={options}
      value={options.find((o) => o.value === value)}
      onChange={(o) => setValue(o?.value)}
      isSearchable={searchable}
      noOptionsMessage={() => t('general.noOptions')}
      {...props}
    />
  );
}
